import type { AppliedDamage, DamageRoll, DamageType } from "../types/combat.js";
import { rollDice } from "./dice.js";

export interface DamageInput {
  diceCount: number;
  diceSides: number;
  modifier: number;
  damageType: DamageType;
  isCriticalHit: boolean;
}

/**
 * Rolls weapon/spell damage dice per 5e SRD rules.
 * Pure function — no side effects.
 * On a critical hit the damage dice are doubled before adding the static modifier.
 */
export function resolveDamage(input: DamageInput): DamageRoll {
  const rollCount = input.isCriticalHit ? input.diceCount * 2 : input.diceCount;
  const dice = rollDice(rollCount, input.diceSides);
  const total = dice.reduce((sum, d) => sum + d, 0) + input.modifier;

  return {
    dice,
    modifier: input.modifier,
    total: Math.max(0, total),
    damageType: input.damageType,
  };
}

/**
 * Subset of `Entity` that `applyDamage` reads. Kept narrow so callers can
 * pass plain test fixtures or any object that exposes HP plus the optional
 * R/V/I tables.
 */
export interface DamageTarget {
  readonly hp: number;
  readonly maxHp: number;
  readonly resistances?: readonly DamageType[];
  readonly vulnerabilities?: readonly DamageType[];
  readonly immunities?: readonly DamageType[];
}

/**
 * Computes the HP result of dealing `rawAmount` damage of `type` to a target.
 * Pure function — returns a new HP value; caller is responsible for mutating the entity.
 *
 * Damage interaction order per the 5e SRD:
 *   1. Immunity → final amount is 0.
 *   2. Otherwise: vulnerability doubles the amount, then resistance halves it
 *      (rounded down). A target that is both vulnerable and resistant ends up
 *      taking the unmodified amount.
 */
export function applyDamage(
  target: DamageTarget,
  rawAmount: number,
  type: DamageType,
): AppliedDamage {
  let appliedAmount: number;
  if (target.immunities?.includes(type)) {
    appliedAmount = 0;
  } else {
    let amount = rawAmount;
    if (target.vulnerabilities?.includes(type)) amount *= 2;
    if (target.resistances?.includes(type)) amount = Math.floor(amount / 2);
    appliedAmount = Math.max(0, amount);
  }
  const newHp = Math.max(0, target.hp - appliedAmount);
  return { rawAmount, appliedAmount, newHp };
}
